import React from 'react'
import { es } from './es'
import { en } from './en'
import { interpolate } from './interpolate'
import type { I18nKey } from './keys'
import {
  type AppLocale,
  APP_INTL_LOCALE,
  readStoredAppLocale,
  writeStoredAppLocale,
  type TranslateVars,
} from './types'

interface I18nContextValue {
  locale: AppLocale
  intlLocale: string
  setLocale: (locale: AppLocale) => void
  t: (key: I18nKey, vars?: TranslateVars) => string
}

const I18nContext = React.createContext<I18nContextValue | null>(null)

function getMessage(locale: AppLocale, key: I18nKey): string {
  if (locale === 'en') {
    return en[key] ?? es[key]
  }
  return es[key]
}

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [locale, setLocaleState] = React.useState<AppLocale>(() => readStoredAppLocale())

  const setLocale = React.useCallback((nextLocale: AppLocale) => {
    setLocaleState(nextLocale)
    writeStoredAppLocale(nextLocale)
  }, [])

  React.useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale
    }
  }, [locale])

  const t = React.useCallback((key: I18nKey, vars?: TranslateVars) => {
    const message = getMessage(locale, key)
    return interpolate(message, vars)
  }, [locale])

  const value = React.useMemo<I18nContextValue>(() => ({
    locale,
    intlLocale: APP_INTL_LOCALE[locale],
    setLocale,
    t,
  }), [locale, setLocale, t])

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18nContext(): I18nContextValue {
  const ctx = React.useContext(I18nContext)
  if (!ctx) {
    throw new Error('useI18n must be used within an I18nProvider')
  }
  return ctx
}
